import type { AgentSnapshot } from "../core/sessionProbe";
import type { ContactSummary } from "../core/contactId";

type Props = {
  agent: AgentSnapshot;
  contacts: ContactSummary[];
  selectedContactId: string | undefined;
  onSelectContact: (contactId: string) => void;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** Reads AgentState.name from getAvailabilityState / getState payloads. */
function stateName(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (!isRecord(value)) return undefined;
  if (typeof value.name === "string") return value.name;
  if (isRecord(value.state) && typeof value.state.name === "string") {
    return value.state.name;
  }
  return undefined;
}

/** Formats `{ CHAT: 5, VOICE: 1 }` as `CHAT 5 · VOICE 1`. */
function concurrencyLabel(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const parts = Object.entries(value)
    .filter(([, n]) => typeof n === "number")
    .map(([k, n]) => `${k} ${n}`);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/**
 * Always-visible agent + contact roster. This is the kit's primary answer to
 * "who is this agent and which contacts are in the workspace right now".
 */
export function Roster({
  agent,
  contacts,
  selectedContactId,
  onSelectContact,
}: Props): React.JSX.Element {
  const availability = stateName(agent.availability) ?? stateName(agent.state);
  const concurrency = concurrencyLabel(agent.concurrency);

  return (
    <section className="roster" aria-label="Workspace roster">
      <div className="roster-agent">
        <span className="roster-kicker">Agent</span>
        <strong className="roster-name">{agent.name ?? "—"}</strong>
        {availability ? <span className="roster-state">{availability}</span> : null}
        {concurrency ? <span className="roster-meta">{concurrency}</span> : null}
        {agent.extension ? (
          <span className="roster-meta">ext {String(agent.extension)}</span>
        ) : null}
      </div>
      <div className="roster-contacts">
        <span className="roster-kicker">Contacts {contacts.length}</span>
        {contacts.length === 0 ? (
          <p className="roster-empty">
            None yet. Incoming, accept, transfer, supervisor monitor/barge, and
            concurrent chats land here.
          </p>
        ) : (
          <ul className="roster-list">
            {contacts.map((c) => (
              <li key={c.contactId}>
                <button
                  type="button"
                  className={
                    c.contactId === selectedContactId ? "roster-chip selected" : "roster-chip"
                  }
                  onClick={() => onSelectContact(c.contactId)}
                >
                  <span className="roster-chip-type">{c.type ?? "contact"}</span>
                  <span className="roster-chip-id">{c.contactId}</span>
                  {c.subtype ? (
                    <span className="roster-chip-sub">{c.subtype}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
